import { forwardRef, useCallback, useEffect, useRef, useState, type CSSProperties, type HTMLAttributes, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Expand, X, Search } from 'lucide-react'
import SectionBadge, { type SezioneCard } from './SectionBadge'

type BSDCardKind = 'panel' | 'kpi' | 'custom'

interface BSDCardProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode
  kind?: BSDCardKind
  sezione?: SezioneCard
  className?: string
  style?: CSSProperties
  label?: ReactNode
  value?: string
  icon?: React.ComponentType<{ size?: string | number; style?: React.CSSProperties }>
  color?: string
  sub?: ReactNode
  zoomSub?: ReactNode
  footer?: ReactNode | ((openZoom: () => void) => ReactNode)
  hideZoomButton?: boolean
  zoomable?: boolean
  zoomSearchable?: boolean
  zoomHeight?: number
}

function getBaseClass(kind: BSDCardKind) {
  if (kind === 'kpi') return 'telemetry-kpi-card'
  if (kind === 'panel') return 'telemetry-panel-card'
  return ''
}

function ZoomableKpiDialogContent({
  kind, label, value, Icon, color, sub, zoomSearchable, zoomHeight, children,
}: {
  kind: BSDCardKind
  label?: ReactNode
  value?: string
  Icon?: React.ComponentType<{ size?: string | number; style?: React.CSSProperties }>
  color?: string
  sub?: ReactNode
  zoomSearchable?: boolean
  zoomHeight?: number
  children?: ReactNode
}) {
  const [search, setSearch] = useState('')
  const listRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!zoomSearchable || !listRef.current) return
    const term = search.toLowerCase()
    // The sub content is typically wrapped in a single grid div — search its children
    const container = listRef.current.children.length === 1
      ? listRef.current.children[0]
      : listRef.current
    const items = container.children
    for (let i = 0; i < items.length; i++) {
      const el = items[i] as HTMLElement
      el.style.display = !term || el.textContent?.toLowerCase().includes(term) ? '' : 'none'
    }
  }, [search, zoomSearchable])

  if (children) {
    return <div className="zoomable-dialog-content" style={zoomHeight ? { minHeight: zoomHeight } : undefined}>{children}</div>
  }

  if (kind !== 'kpi' || !label || !value) return null

  return (
    <div className="zoomable-dialog-content" style={{ display: 'flex', flexDirection: 'column', minHeight: zoomHeight, maxHeight: '65vh' }}>
      <div className="telemetry-kpi-head" style={{ flexShrink: 0 }}>
        <div>
          <div className="telemetry-kpi-label">{label}</div>
          <p className="telemetry-kpi-value">{value}</p>
        </div>
        {Icon && color ? (
          <div className="telemetry-kpi-icon" style={{ backgroundColor: `${color}20`, flexShrink: 0 }}>
            <Icon size={21} style={{ color }} />
          </div>
        ) : null}
      </div>
      {zoomSearchable && (
        <div style={{ flexShrink: 0, padding: '8px 0', position: 'relative' }}>
          <Search size={14} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }} />
          <input
            type="text"
            placeholder="Cerca…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%',
              padding: '6px 8px 6px 28px',
              fontSize: 12,
              border: '1px solid var(--border)',
              borderRadius: 6,
              background: 'var(--surface-2, var(--background))',
              color: 'var(--text-1)',
              outline: 'none',
            }}
          />
        </div>
      )}
      {sub ? (
        <div ref={listRef} className="telemetry-kpi-sub" style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
          {sub}
        </div>
      ) : null}
    </div>
  )
}

const BSDCard = forwardRef<HTMLDivElement, BSDCardProps>(function BSDCard(
  { children, kind = 'panel', sezione, className, style, label, value, icon: Icon, color, sub, zoomSub, footer, hideZoomButton = false, zoomable = false, zoomSearchable = false, zoomHeight, ...rest },
  ref,
) {
  const zoomButtonRef = useRef<HTMLButtonElement | null>(null)
  const [isZoomRendered, setIsZoomRendered] = useState(false)
  const [isZoomOpen, setIsZoomOpen] = useState(false)
  const [zoomOrigin, setZoomOrigin] = useState<{ x: number; y: number }>({ x: 0, y: 0 })

  const baseClass = getBaseClass(kind)
  const resolvedClassName = [baseClass, zoomable ? 'zoomable-chart-card' : '', className].filter(Boolean).join(' ')
  const isMobileView = typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
  const hasComplexSub = kind === 'kpi' && !!sub && typeof sub !== 'string'
  const baseStyle: CSSProperties = kind === 'custom'
    ? {}
    : isMobileView
      ? kind === 'kpi'
        ? { minHeight: hasComplexSub ? '280px' : 'auto', height: 'auto', display: 'flex', flexDirection: 'column' }
        : { minHeight: 'auto', height: 'auto' }
      : kind === 'kpi'
        ? { height: 'var(--bsd-card-height, 250px)', display: 'flex', flexDirection: 'column' }
        : { height: 'var(--bsd-card-height, 250px)' }
  // On mobile, strip fixed height from style prop so cards adapt to content
  const mergedStyle = isMobileView && kind !== 'custom' && style
    ? { ...style, height: 'auto' }
    : style
  const resolvedStyle: CSSProperties = { ...baseStyle, ...mergedStyle }

  useEffect(() => {
    if (!isZoomRendered || typeof document === 'undefined') return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [isZoomRendered])

  useEffect(() => {
    if (!isZoomRendered) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsZoomOpen(false)
        window.setTimeout(() => setIsZoomRendered(false), 340)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isZoomRendered])

  const openZoom = () => {
    const rect = zoomButtonRef.current?.getBoundingClientRect()
    if (rect) {
      setZoomOrigin({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 })
    } else {
      setZoomOrigin({ x: window.innerWidth / 2, y: window.innerHeight / 2 })
    }
    setIsZoomRendered(true)
    requestAnimationFrame(() => setIsZoomOpen(true))
  }

  const closeZoom = () => {
    setIsZoomOpen(false)
    window.setTimeout(() => setIsZoomRendered(false), 340)
  }

  return (
    <>
      <div ref={ref} className={resolvedClassName || undefined} style={resolvedStyle} {...rest}>
        {sezione ? <SectionBadge sezione={sezione} /> : null}
        {zoomable && !hideZoomButton && (kind === 'panel' || kind === 'kpi') ? (
          <button
            ref={zoomButtonRef}
            type="button"
            className="zoomable-chart-zoom-btn"
            onClick={openZoom}
            aria-label="Ingrandisci grafico"
            title="Ingrandisci grafico"
          >
            <Expand size={14} />
          </button>
        ) : null}
        {kind === 'kpi' && !children && label && value ? (
          <>
            <div className="telemetry-kpi-head">
              <div>
                <div className="telemetry-kpi-label">{label}</div>
                <p className="telemetry-kpi-value">{value}</p>
              </div>
              {Icon && color ? (
                <div className="telemetry-kpi-icon" style={{ backgroundColor: `${color}20`, flexShrink: 0 }}>
                  <Icon size={21} style={{ color }} />
                </div>
              ) : null}
            </div>
            {sub ? (
              <div
                className="telemetry-kpi-sub"
                style={{
                  display: 'block',
                  width: 'calc(100% + 8px)',
                  marginRight: -8,
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  paddingRight: 4,
                }}
              >
                {sub}
              </div>
            ) : null}
            {color ? <div className="telemetry-kpi-line" style={{ backgroundColor: color }} /> : null}
          </>
        ) : children}
        {footer ? (
          <div style={{ flexShrink: 0, marginTop: 'auto' }}>
            {typeof footer === 'function' ? footer(openZoom) : footer}
          </div>
        ) : null}
      </div>

      {isZoomRendered && typeof document !== 'undefined'
        ? createPortal(
          <div
            className={`zoomable-overlay ${isZoomOpen ? 'zoomable-overlay--open' : 'zoomable-overlay--closing'}`}
            onClick={closeZoom}
            role="presentation"
          >
            <div
              className={`zoomable-dialog ${isZoomOpen ? 'zoomable-dialog--open' : ''}`}
              style={{
                ['--zoom-from-x' as any]: `${zoomOrigin.x}px`,
                ['--zoom-from-y' as any]: `${zoomOrigin.y}px`,
              }}
              onClick={(event) => event.stopPropagation()}
              role="dialog"
              aria-modal="true"
            >
              <button type="button" className="zoomable-dialog-close" onClick={closeZoom} aria-label="Chiudi dialog">
                <X size={16} />
              </button>
              <ZoomableKpiDialogContent
                kind={kind}
                label={label}
                value={value}
                Icon={Icon}
                color={color}
                sub={zoomSub ?? sub}
                zoomSearchable={zoomSearchable}
                zoomHeight={zoomHeight}
              >
                {children}
              </ZoomableKpiDialogContent>
            </div>
          </div>,
          document.body,
        )
        : null}
    </>
  )
})

export default BSDCard
